import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  Checkbox,
} from '@mui/material'
import { Control, Controller } from 'react-hook-form'
import { Box, Chip, Stack, Typography, IconButton } from '@mui/material_modern'
import ClearIcon from '@mui/icons-material/Clear'

export interface SelectItem {
  id: string
  name: string | React.ReactNode
  description?: string
  isHeader?: boolean
  disabled?: boolean
}

interface SelectProps {
  name: string
  label?: string | null
  control: Control<any>
  helperText?: string
  variant?: string
  defaultValue?: string | string[]
  items: SelectItem[]
  className?: string | null
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  selectProps?: React.ComponentProps<typeof MuiSelect>
  InputLabelProps?: React.ComponentProps<typeof InputLabel>
  useChips?: boolean
  useCheckboxes?: boolean
  clearable?: boolean
  [key: string]: any
}

const Select: React.FC<SelectProps> = ({
  name,
  label,
  control,
  helperText,
  variant = 'outlined',
  defaultValue,
  items,
  className,
  inputProps,
  selectProps,
  InputLabelProps,
  useChips = false,
  useCheckboxes = false,
  clearable = false,
  ...restProps
}) => {
  const labelId = `${name}-label`

  return (
    <FormControl
      sx={{
        width: '100%',
        '& .MuiInputBase-adornedEnd': {
          paddingRight: 0,
        },
      }}
      {...restProps}
      variant={variant as any}
    >
      {label && (
        <InputLabel id={labelId} {...InputLabelProps}>
          {label}
        </InputLabel>
      )}

      <Controller
        rules={{ shouldDirty: true }}
        render={({ field }) => (
          <>
            <MuiSelect
              {...field}
              labelId={labelId}
              className={className}
              inputProps={inputProps}
              {...(label && { label })}
              {...selectProps}
              endAdornment={
                clearable && field.value ? (
                  <IconButton
                    sx={{ position: 'absolute', right: '32px' }}
                    onClick={e => {
                      e.stopPropagation()
                      field.onChange('')
                    }}
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  selectProps?.endAdornment
                )
              }
              renderValue={
                useChips
                  ? (selected: any) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(value => (
                          <Chip key={value} label={items.find(option => option.id === value)?.name || value} />
                        ))}
                      </Box>
                    )
                  : undefined
              }
            >
              {items.map(item =>
                item?.isHeader ? (
                  <ListSubheader key={`subheader-${item?.id}`}>{item?.name}</ListSubheader>
                ) : (
                  <MenuItem key={`item-${item?.id}`} value={item?.id} disabled={item?.disabled}>
                    {useCheckboxes && <Checkbox checked={field.value.indexOf(item?.id) > -1} />}
                    <Stack>
                      <Typography>{item?.name}</Typography>
                      {item?.description && (
                        <Typography variant="caption" color={item?.disabled ? 'error' : 'text.secondary'}>
                          {item?.description}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ),
              )}
            </MuiSelect>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </>
        )}
        name={name}
        control={control}
        defaultValue={defaultValue}
      />
    </FormControl>
  )
}

export default Select
